import type { ErrorResponse } from '../../modules.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, mergeMap, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { UtilityService } from 'app/shared/services/utility.service';
import { withLocalStorage } from 'app/shared/store-features/localstorage.feature';
import { withSearch } from 'app/shared/store-features/search.feature';
import type { AddApiMapping, ApplyApiMapping, DeleteApiMapping, EditApiMapping } from './api-mapping.model';
import { ApiMappingsService } from '../../../../../api/services';
import type { AvailableApiResponse } from '../../../../../api/models/available-api-response';
import type { ApiMappingResponse } from '../../../../../api/models/api-mapping-response';
import { ModulesStore } from '../../modules.store';

type ApiMappingState = {
  apiMappings: Record<string, ApiMappingResponse[]>;
  availableApis: AvailableApiResponse[];
  isLoading: boolean;
  loaded: boolean;
  pluralName: string;
  searchTerm: string;
  singularName: string;
  slug: string;
  storeName: string;
  error?: string;
};

const initialState: ApiMappingState = {
  apiMappings: {},
  availableApis: [],
  isLoading: true,
  loaded: false,
  pluralName: 'API mappings',
  searchTerm: '',
  singularName: 'API mapping',
  slug: 'api-mappings',
  storeName: 'apiMappings',
};

export const ApiMappingsStore = signalStore(
  { providedIn: 'root' },
  withState<ApiMappingState>(initialState),
  withLocalStorage(),
  withSearch(),
  withComputed(({ apiMappings, searchTerm }) => ({
    apiMappingsFiltered: computed(() => {
      // Flatten mappings object and filter based on search term
      const allMappings = Object.values(apiMappings()).flat();
      return allMappings.filter(mapping => UtilityService.searchObject(mapping, searchTerm()));
    }),
  })),
  withMethods(store => {
    const apiMappingsService = inject(ApiMappingsService);
    const modulesStore = inject(ModulesStore);
    const utilityService = inject(UtilityService);

    return {
      loadApiMappings: rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, { isLoading: true });
            return apiMappingsService.getApiMappings().pipe(
              tapResponse({
                next: (mappings: ApiMappingResponse[]) => {
                  // Group mappings by moduleCode
                  const mappingsByModule = mappings.reduce(
                    (acc, mapping) => {
                      const moduleId = modulesStore.getModuleByCode(mapping.moduleCode)?.id;
                      if (moduleId == null) {
                        return acc;
                      }
                      if (!acc[moduleId]) {
                        acc[moduleId] = [];
                      }
                      acc[moduleId].push(mapping);
                      return acc;
                    },
                    {} as Record<string, ApiMappingResponse[]>
                  );

                  patchState(store, {
                    apiMappings: mappingsByModule,
                    isLoading: false,
                    loaded: true,
                    error: undefined,
                  });
                },
                error: (e: ErrorResponse) => {
                  patchState(store, {
                    isLoading: false,
                    error: UtilityService.createErrorMessage(e),
                  });
                  utilityService.notify('Fout bij laden van API mappings');
                  console.error(e);
                },
              })
            );
          })
        )
      ),

      add: rxMethod<AddApiMapping>(
        pipe(
          mergeMap(({ moduleId, data }) => {
            return apiMappingsService.createApiMapping({ body: data }).pipe(
              tapResponse({
                next: response => {
                  patchState(store, state => ({
                    apiMappings: {
                      ...state.apiMappings,
                      [moduleId]: [...(state.apiMappings[moduleId] || []), response],
                    },
                    error: undefined,
                  }));
                  utilityService.notify('API mapping toegevoegd');
                },
                error: (e: ErrorResponse) => {
                  patchState(store, { error: UtilityService.createErrorMessage(e) });
                  utilityService.notify('Fout bij toevoegen van API mapping');
                  console.error(e);
                },
              })
            );
          })
        )
      ),

      edit: rxMethod<EditApiMapping>(
        pipe(
          mergeMap(({ moduleId, mappingId, data }) => {
            return apiMappingsService.updateApiMapping({ body: data }).pipe(
              tapResponse({
                next: response => {
                  patchState(store, state => ({
                    apiMappings: {
                      ...state.apiMappings,
                      [moduleId]: state.apiMappings[moduleId].map(mapping =>
                        mapping.id === mappingId ? response : mapping
                      ),
                    },
                    error: undefined,
                  }));
                  utilityService.notify('API mapping bijgewerkt');
                },
                error: (e: ErrorResponse) => {
                  patchState(store, { error: UtilityService.createErrorMessage(e) });
                  utilityService.notify('Fout bij bijwerken van API mapping');
                  console.error(e);
                },
              })
            );
          })
        )
      ),

      delete: rxMethod<DeleteApiMapping>(
        pipe(
          mergeMap(({ moduleId, mappingId }) => {
            return apiMappingsService.deleteApiMapping({ apiMappingId: mappingId }).pipe(
              tapResponse({
                next: () => {
                  patchState(store, state => ({
                    apiMappings: {
                      ...state.apiMappings,
                      [moduleId]: state.apiMappings[moduleId].filter(mapping => mapping.id !== mappingId),
                    },
                    error: undefined,
                  }));
                  utilityService.notify('API mapping verwijderd');
                },
                error: (e: ErrorResponse) => {
                  patchState(store, { error: UtilityService.createErrorMessage(e) });
                  utilityService.notify('Fout bij verwijderen van API mapping');
                  console.error(e);
                },
              })
            );
          })
        )
      ),

      getAvailableApis: rxMethod<void>(
        pipe(
          switchMap(() => {
            return apiMappingsService.getAvailableApis().pipe(
              tapResponse({
                next: response => {
                  patchState(store, state => ({
                    availableApis: response,
                  }));
                },
                error: (e: ErrorResponse) => {
                  utilityService.notify('Fout bij ophalen van beschikbare APIs');
                  console.error(e);
                },
              })
            );
          })
        )
      ),
      applyMapping(params: ApplyApiMapping) {
        if (params.entry.selectedInput) delete params.entry.selectedInput;

        return apiMappingsService
          .applyApiMappingRaw({
            apiMappingId: params.mappingId,
            moduleId: params.moduleId,
            body: params.entry,
          })
          .pipe(
            tap(() => {
              if (params.message) {
                utilityService.notify('API mapping succesvol toegepast');
              }
            })
          );
      },
    };
  }),
  withHooks({
    onInit({ loadLocal, storeName, loadApiMappings, getAvailableApis }) {
      loadLocal(storeName());
      loadApiMappings();
      getAvailableApis();
    },
  })
);
